export interface CashOutReasonChoice {
  text: string;
  value: string;
  imageName: string;
  default?: boolean;
}

export const CASH_OUT_REASON_CHOICES: CashOutReasonChoice[] = [
  { text: 'Pay off high-interest debt', value: 'reduce-debt', imageName: 'interest-debt', default: true },
  { text: 'Home improvement', value: 'home-improvement', imageName: 'home-improvement' },
  { text: 'Buy another property', value: 'investment', imageName: 'investment-property' },
  { text: 'Start or grow a business', value: 'investment', imageName: 'cash-out' },
  { text: 'Education', value: 'education', imageName: 'education' },
  { text: 'Medical expenses', value: 'medical', imageName: 'medical' },
  { text: 'Emergency use', value: 'emergency', imageName: 'emergency-use' },
  { text: 'Another big purchase', value: 'extra-cash', imageName: 'cash-out' },
  { text: 'Other', value: 'other', imageName: 'dont-know' },
];

export const CASH_OUT_REASON_DETAIL_MAX_LENGTH = 255;

/** Recognize cash-use wording that names another property rather than a business or generic investment. */
export function isPropertyPurchaseCashOutReason(raw: unknown): boolean {
  const value = (raw === null || raw === undefined ? '' : raw.toString())
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return /\b(second|another|vacation|investment|rental)\b(?:[a-z ]{0,20})\b(home|house|property|place)\b/.test(value);
}

/** Keep the consumer's words, removing only unsafe controls and over-column overflow. */
export function exactCashOutReason(raw: unknown, fallback: unknown = ''): string {
  const candidate = raw === null || raw === undefined || raw.toString().trim() === '' ? fallback : raw;
  return (candidate === null || candidate === undefined ? '' : candidate.toString())
    .replace(/[\u0000-\u001f\u007f]/g, ' ')
    .trim()
    .slice(0, CASH_OUT_REASON_DETAIL_MAX_LENGTH);
}

/** Resolve only unambiguous reason statements; questions are rejected by the caller first. */
export function canonicalCashOutReasonFromText(raw: unknown): string {
  const value = (raw === null || raw === undefined ? '' : raw.toString())
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!value) { return ''; }

  if (isPropertyPurchaseCashOutReason(value)
      || /\b(business|company|startup|start a business)\b/.test(value)) {
    return 'investment';
  }
  if (/\bhome improvements?\b/.test(value)
      || /\b(?:renovat[a-z]*|remodel[a-z]*|repair(?:s|ed|ing)?|fix(?:ed|ing)?(?: up)?)(?:[a-z ]{0,20})\b(?:home|house|kitchen|bathroom|roof)\b/.test(value)
      || /\b(?:home|house|kitchen|bathroom|roof)\b(?:[a-z ]{0,20})\b(?:renovat[a-z]*|remodel[a-z]*|repair(?:s|ed|ing)?|fix(?:ed|ing)?(?: up)?)\b/.test(value)) {
    return 'home-improvement';
  }
  if (/\b(education|tuition|college|school|student loan)\b/.test(value)) { return 'education'; }
  if (/\b(medical|hospital|doctor|dental|surgery|health care|healthcare)\b/.test(value)) { return 'medical'; }
  if (/\b(emergency|urgent expense|unexpected expense)\b/.test(value)) { return 'emergency'; }
  if (/\b(debt|credit card|high interest|consolidat|pay off (?:my )?(?:cards|loans))\b/.test(value)) {
    return 'reduce-debt';
  }
  if (/\b(car|vehicle|wedding|large purchase|big purchase|extra cash)\b/.test(value)) { return 'extra-cash'; }
  if (/\brenovat[a-z]*\b/.test(value)) { return 'home-improvement'; }
  return '';
}
